import React, { useState, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";

import { Notification, NOTIFICATION_TYPES, StepperFormRow } from "@components";
import { stepperFormValidationMapper, validateIdentification, trb, i18 } from "@utilities";
import { getStep3Validations } from "../../Validations/step3";
import useFormStepControl from "@hooks/useFormStepControl";
import { ActionButtons, ExtraCheckModal } from "../../Components";
import { WhoOpenAccount } from "./groups/WhoOpenAccount";
import { ClientCeo } from "./groups/ClientCeo";
import { ClientCeoAddress } from "./groups/ClientCeoAddress";
import { ClientCeoDocs } from "./groups/ClientCeoDocs";
import { ClientRep } from "./groups/ClientRep";
import { ClientRepAddress } from "./groups/ClientRepAddress";
import { ClientRepDocs } from "./groups/ClientRepDocs";
import { selectIsFormFilledByCeo } from "@redux/Selectors/KYC";

const getModalVisibleData = (formInfo, isCeoFilling) =>
  Object.assign({}, formInfo.clientCeo[0].data, !isCeoFilling ? formInfo.clientRep[0].data : {});

const OTHER_COUNTRIES_KEYS = [
  "clientCeo.0.data.otherCeoCitDocTy",
  "clientCeo.0.data.otherCeoCitDocNo",
  "clientCeo.0.data.otherCeoCitDocCountry",
  "clientCeo.0.data.otherCeoCitizienSch"
];

const REP_KEYS = [
  "clientRep.0.data.clientRepName",
  "clientRep.0.data.clientRepSurname",
  "clientRep.0.data.clientRepId",
  "clientRep.0.data.clientRepRegCountry",
  "clientRep.0.data.clientRepBirthDate",
  "clientRep.0.data.clientRepCountry",
  "clientRep.0.data.clientRepCity",
  "clientRep.0.data.clientRepStreet",
  "clientRep.0.data.clientRepPhoneNo",
  "clientRep.0.data.clientRepIdDocType",
  "clientRep.0.data.clientRepDocCountry",
  "clientRep.0.data.clientRepDocIssuePlace",
  "clientRep.0.data.clientRepDocIssueDate",
  "clientRep.0.data.clientRepDocNo",
  "clientRep.0.data.clientRepDocValidDate",
  "clientRep.0.data.clientRepMandatValidDate"
];

export const FormStep3 = () => {
  const {
    formDraft,
    submitForm,
    isSent,
    handleActionButtonClick,
    submitType,
    validateForm
  } = useFormStepControl();
  const isFilledByCeo = useSelector(selectIsFormFilledByCeo);

  const { handleSubmit, control, setError, unregister } = useForm({
    resolver: (values, context) =>
      validateForm(values, context, () =>
        stepperFormValidationMapper(
          values,
          getStep3Validations({
            otherCeoCitizen: values.clientCeo[0].data.otherCeoCitizien,
            showRepFields: !isFilledByCeo && values.clientProfile[0].data.whoOpenAccount !== "CMP1"
          })
        )
      ),
    mode: "onSubmit",
    context: {
      submitType
    },
    reValidateMode: "onChange",
    shouldFocusError: true,
    defaultValues: formDraft.personData
  });

  const formFilledBy = useWatch({
    control,
    name: "clientProfile.0.data.whoOpenAccount"
  });

  const [formInfoEntity, setFormInfoEntity] = useState();
  const [isValidatingId, setIsValidatingId] = useState(false);

  const sawIdErrorOnce = useRef(false);

  const onConfirm = () => {
    submitForm(formInfoEntity, "next");
  };

  const openCheckModal = async data => {
    if (
      !isFilledByCeo &&
      formFilledBy !== "CMP1" &&
      !sawIdErrorOnce.current &&
      submitType === "next"
    ) {
      try {
        setIsValidatingId(true);
        const idError = await validateIdentification({
          countryValidation: "LT", // ask about what country to list
          identificationCode: data.clientCeo[0].data.clientCeoId,
          countryValidationType: "PersonalIdentificationNumber"
        });
        if (idError) {
          setError("clientCeo.0.data.clientCeoId", idError, { shouldFocus: true });
          sawIdErrorOnce.current = true;
          return;
        }
      } catch (e) {
        Notification({
          type: NOTIFICATION_TYPES.ERROR,
          message: trb(i18.NotificationMessages.SomethingWentWrong)
        });
      } finally {
        setIsValidatingId(false);
      }
    }

    if (submitType === "prev") {
      return submitForm(data, submitType);
    }
    setFormInfoEntity(data);
  };

  const resetOtherCeoPlace = e => {
    if (e.target.value === 1) return;
    unregister(OTHER_COUNTRIES_KEYS);
  };

  const resetRepFields = value => {
    if (value === "CMP1") {
      unregister(REP_KEYS);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(openCheckModal)}>
        <WhoOpenAccount
          control={control}
          isFilledByCeo={isFilledByCeo}
          resetRepFields={resetRepFields}
        />
        <ClientCeo control={control} resetOtherCeoPlace={resetOtherCeoPlace} />
        <StepperFormRow>
          <ClientCeoAddress control={control} />
          <ClientCeoDocs control={control} />
        </StepperFormRow>
        {!isFilledByCeo && formFilledBy !== "CMP1" && (
          <>
            <ClientRep control={control} />
            <StepperFormRow>
              <ClientRepAddress control={control} />
              <ClientRepDocs control={control} />
            </StepperFormRow>
          </>
        )}

        <ActionButtons
          isLoading={isSent || isValidatingId}
          handlePrevStep={handleActionButtonClick}
          handleNextStep={handleActionButtonClick}
        />
      </form>
      {formInfoEntity && (
        <ExtraCheckModal
          onClose={() => setFormInfoEntity()}
          onConfirm={onConfirm}
          formData={getModalVisibleData(formInfoEntity, isFilledByCeo || formFilledBy === "CMP1")}
          isLoading={isSent}
        />
      )}
    </>
  );
};
